import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { submitFeedback } from '@/api/feedback';
import { useTranslation } from '@/i18n';
import { FreeTextAnswer } from './FreeTextAnswer';
import { RatingScale, type Rating } from './RatingScale';
import { ratingIconFrom } from './ratingIcon';
import type { FeedbackCampaign, FeedbackQuestion, SubmitFeedbackRequest } from '@/api/types';

interface FeedbackPanelProps {
    campaign: FeedbackCampaign;
    accessToken: string;
    /** Called when the guest skips, so the receipt can put the prompt away. */
    onDismiss: () => void;
}

/**
 * The restaurant's own questions, asked in the app instead of sending the guest to a vendor's survey.
 *
 * This is the whole point of TouchTab being a feedback vendor rather than a link: a guest who has just paid is
 * already here, and every hop to somewhere else loses most of them. It mirrors the handheld's
 * `FeedbackComponent` — the same instruction line, the same five-face scale — so the two collect the same
 * thing. Free text is the one place they have diverged: this app asks for it and the handheld does not yet.
 *
 * Answers are optional, individually. A guest who rates one question and not the others sends one answer;
 * that is a real opinion and worth more than nothing. Sending no answers at all is not a submission, so the
 * button says "Skip" until something is answered and closing the prompt writes nothing.
 *
 * Up to five questions now, where it used to be three. Nothing here caps it and nothing needs to: the panel
 * is a column in the receipt page's normal flow, so a longer campaign makes the page taller and the page
 * already scrolls. What five questions do change is how far the Send button sits from the top of the card,
 * which is why the comment boxes are four rows rather than as tall as they would like to be.
 */
export function FeedbackPanel({ campaign, accessToken, onDismiss }: FeedbackPanelProps) {
    const { t } = useTranslation();
    const [ratings, setRatings] = useState<Record<string, Rating | null>>({});
    const [comments, setComments] = useState<Record<string, string>>({});
    const headingRef = useRef<HTMLHeadingElement>(null);

    // The prompt appears below a receipt the guest has been reading, so it is easy to miss that anything
    // happened. Moving focus to its heading puts a screen reader and the keyboard where the new content is.
    useEffect(() => headingRef.current?.focus(), []);

    const commentOn = (question: FeedbackQuestion) => (comments[question.id] ?? '').trim();

    /*
     * What gets sent, which is not the same as what the guest engaged with.
     *
     * A rating question contributes only when it was rated. A free-text question contributes ALWAYS - as an
     * empty string when the guest typed nothing - because that is the shape the API documents for a skipped
     * comment, and because "was asked and declined" and "was never asked" are different facts a report should
     * be able to tell apart. Nothing is sent at all unless `hasAnswered` below, so an untouched panel still
     * writes nothing.
     *
     * The rating of 0 on a text answer is not a rating. It is the field's default, and `textAnswer` being
     * present is what tells the API to skip the 1-5 range check on it.
     */
    const answers: SubmitFeedbackRequest['answers'] = campaign.questions.flatMap(
        (question): SubmitFeedbackRequest['answers'] => {
            if (isFreeText(question)) {
                return [{ questionId: question.id, rating: 0, textAnswer: commentOn(question) }];
            }

            const rating = ratings[question.id];
            return rating != null ? [{ questionId: question.id, rating }] : [];
        },
    );

    // Read once for the whole panel: every rating question in a campaign is drawn the brand's way.
    const icon = ratingIconFrom(campaign.ratingIcon);

    /*
     * Whether the guest actually did something, which is what the button reads from.
     *
     * Deliberately not `answers.length`: once a campaign carries a free-text question that count is never
     * zero, so a guest who touched nothing would be offered "Send" and would post a row of empty strings.
     * Whitespace does not count either - a stray space from a keyboard is not an opinion.
     */
    const hasAnswered =
        campaign.questions.some((question) =>
            isFreeText(question) ? commentOn(question) !== '' : ratings[question.id] != null,
        );

    const submission = useMutation({
        /*
         * `askedQuestionIds` is every question this panel rendered, which is the whole campaign it was
         * handed - the panel never shows a subset. It is sent because a skipped rating is absent from
         * `answers`, so without it the server cannot tell a question the guest declined from one they were
         * never shown. Campaigns rotate now, so that is most questions on most responses.
         *
         * From `campaign`, the prop this panel renders from, and never a fresh fetch: the server draws a new
         * set on every GET, so a second fetch would describe a survey the guest never saw.
         */
        mutationFn: () =>
            submitFeedback(accessToken, {
                campaignId: campaign.campaignId,
                answers,
                askedQuestionIds: campaign.questions.map((question) => question.id),
            }),
    });

    if (submission.isSuccess) {
        return (
            <section
                aria-label={t('feedback.title')}
                className="rounded-2xl border border-border bg-background p-5 text-center"
            >
                {/*
                  * `status` rather than `alert`: the guest caused this and is looking at it, so it needs
                  * announcing but not interrupting.
                  */}
                <p role="status" className="font-medium text-pretty">
                    {t('feedback.thanks')}
                </p>
            </section>
        );
    }

    return (
        <section
            aria-labelledby="feedback-heading"
            className="flex flex-col gap-5 rounded-2xl border border-border bg-background p-5"
        >
            <div className="flex flex-col gap-1">
                {/*
                  * `tabIndex={-1}` makes the heading focusable programmatically without adding it to the tab
                  * order, which is what the focus move above needs and all it needs.
                  */}
                <h2
                    id="feedback-heading"
                    ref={headingRef}
                    tabIndex={-1}
                    className="text-lg font-semibold tracking-tight text-balance"
                >
                    {t('feedback.title')}
                </h2>

                {/* The restaurant's own words, so it is not translated — see the note in `messages.ts`. */}
                <p className="text-[0.9375rem] text-muted-foreground text-pretty">{campaign.instruction}</p>
            </div>

            {campaign.questions.map((question) =>
                isFreeText(question) ? (
                    <FreeTextAnswer
                        key={question.id}
                        question={question.name}
                        value={comments[question.id] ?? ''}
                        onChange={(value) => setComments((current) => ({ ...current, [question.id]: value }))}
                        disabled={submission.isPending}
                    />
                ) : (
                    <RatingScale
                        key={question.id}
                        name={`feedback-${question.id}`}
                        question={question.name}
                        icon={icon}
                        value={ratings[question.id] ?? null}
                        onChange={(rating) => setRatings((current) => ({ ...current, [question.id]: rating }))}
                        disabled={submission.isPending}
                    />
                ),
            )}

            {submission.isError && (
                /*
                 * `alert`, unlike the thank-you: the guest pressed send and believes they are done, so this
                 * has to interrupt. Their answers are still on screen and the button still works.
                 */
                <p role="alert" className="text-[0.9375rem] font-medium text-destructive text-pretty">
                    {t('feedback.error')}
                </p>
            )}

            <div className="flex flex-col gap-2">
                <button
                    type="button"
                    onClick={() => (hasAnswered ? submission.mutate() : onDismiss())}
                    disabled={submission.isPending}
                    className="inline-flex min-h-12 items-center justify-center rounded-xl bg-primary px-5 py-3 font-semibold text-primary-foreground shadow-xs disabled:opacity-60"
                >
                    {submission.isPending
                        ? t('feedback.sending')
                        : hasAnswered
                          ? t('feedback.send')
                          : t('feedback.skip')}
                </button>

                {hasAnswered && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        disabled={submission.isPending}
                        className="inline-flex min-h-11 items-center justify-center rounded-xl px-5 py-2 text-[0.9375rem] font-medium text-muted-foreground"
                    >
                        {t('feedback.skip')}
                    </button>
                )}
            </div>
        </section>
    );
}

/*
 * Anything not explicitly FreeText is a rating scale, which is what GuestPayAPI itself does when it maps
 * FeedbackIntegration's enum, and what an absent field has to mean: RatingScale is the zero value on both
 * sides, so a service deployed before free text existed sends nothing here and must still get a scale.
 */
function isFreeText(question: FeedbackQuestion) {
    return question.questionType === 'FreeText';
}
